import { Component, Inject } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { UpfrontLicensePurchaseDialogData } from "../../billing.interface";
import { ShowOnSubmitAttemptErrorStateMatcher } from "../../../shared/error-state-matcher";

@Component({
  selector: "app-upfront-license-purchase-dialog",
  templateUrl: "./upfront-license-purchase-dialog.component.html",
  styleUrls: ["./upfront-license-purchase-dialog.component.scss"],
  standalone: false,
})
export class UpfrontLicensePurchaseDialogComponent {
  public sendingRequest = false;
  public calculatedPrice = 0;
  public calculatedPriceWithVat = 0;
  public vatAmount = 0;
  public employeeCount: number;
  public errorStateMatcher = new ShowOnSubmitAttemptErrorStateMatcher();

  get minLicenseCount(): number {
    return this.data.billingMode === "legacy" ? 0 : 10;
  }

  get hasLicenseCountError(): boolean {
    return (
      this.errorStateMatcher.submitAttempted &&
      this.employeeCount < this.minLicenseCount
    );
  }

  get hasVat(): boolean {
    return (this.data.vatRate ?? 0) > 0;
  }

  get pricePerLicense(): number {
    const raw =
      this.data.subscriptionPeriod === "yearly"
        ? this.data.price * 12
        : this.data.price;
    return parseFloat(raw.toFixed(2));
  }

  constructor(
    public dialogRef: MatDialogRef<UpfrontLicensePurchaseDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: UpfrontLicensePurchaseDialogData,
  ) {
    const initialCount = this.data.defaultEmployeeCount || 0;
    const isLegacyBilling = this.data.billingMode === "legacy";
    this.employeeCount = isLegacyBilling
      ? initialCount
      : Math.max(initialCount, 10);
    this.calculatePrice();
  }

  calculatePrice = () => {
    const isLegacyBilling = this.data.billingMode === "legacy";
    const effectiveEmployeeCount = isLegacyBilling
      ? this.employeeCount
      : Math.max(this.employeeCount, 10);
    const basePrice = this.data.price || 0;
    const multiplier = this.data.subscriptionPeriod === "yearly" ? 12 : 1;
    this.calculatedPrice = parseFloat(
      (basePrice * effectiveEmployeeCount * multiplier).toFixed(2),
    );

    const vatRate = this.data.vatRate ?? 0;
    if (vatRate > 0) {
      this.vatAmount = parseFloat(
        (this.calculatedPrice * (vatRate / 100)).toFixed(2),
      );
      this.calculatedPriceWithVat = parseFloat(
        (this.calculatedPrice + this.vatAmount).toFixed(2),
      );
    } else {
      this.vatAmount = 0;
      this.calculatedPriceWithVat = this.calculatedPrice;
    }
  };

  submit = () => {
    this.errorStateMatcher.submitAttempted = true;
    if (this.data.price <= 0) {
      return;
    }

    const isLegacyBilling = this.data.billingMode === "legacy";
    const minLicenseCount = isLegacyBilling ? 0 : 10;

    if (this.employeeCount < minLicenseCount) {
      return;
    }

    this.sendingRequest = true;
    const effectiveEmployeeCount = isLegacyBilling
      ? this.employeeCount
      : Math.max(this.employeeCount, 10);
    this.dialogRef.close({
      numberOfEmployees: effectiveEmployeeCount,
      proceed: true,
    });
  };

  close = () => this.dialogRef.close({ proceed: false });
}
